import { mdiPageFirst, mdiPageLast } from '@mdi/js';
import Icon from '@mdi/react';
import { Button, Divider, Stack, SxProps } from '@mui/material';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { ceraProBold, vars } from '../../../Theme';
import useBreakpoints from '../../../hooks/useBreakpoint';

/** Timer that controls the hover sidebar. */
let timer: NodeJS.Timeout;

/**
 * Component that renders the Sidebar's header.
 */
const SidebarHeader = (props: { children: JSX.Element | JSX.Element[]; divider?: boolean }) => (
    <Stack direction='column' sx={{ m: 1, position: 'relative' }} flexGrow={0}>
        <Stack direction='row' justifyContent='center' spacing={1} sx={{ width: '100%' }}>
            {props.children}
        </Stack>

        {props.divider && <Divider sx={{ mt: 1 }} flexItem />}
    </Stack>
);

/**
 * Gets the styles for the "all data" button.
 * @param active - Button active state.
 */
const getBtnSx = (active: boolean): SxProps => ({
    bgcolor: active ? vars.bgGreen : 'transparent',
    color: active ? vars.brandGreen : vars.black,
    justifyContent: 'left',
    textTransform: 'none',
    ...ceraProBold,
    fontSize: 16,
    flexGrow: 4,
    height: 44,
});

/**
 * Styles for the toggle button.
 */
const toggleBtnSx: SxProps = {
    minWidth: 'unset',
    color: vars.black,
    height: 44,
    width: 44,
    px: 1.25,
};

/**
 * Customisable Application Sidebar.
 */
export default (props: ISidebarProps) => {
    /** Configure component translation. */
    const { t } = useTranslation('translations', { keyPrefix: 'noVulnerabilitiesInfo' });

    /** Determine screen size. */
    const { isDesktop } = useBreakpoints();

    /** Deconstruct props. */
    const { btnConfig, openState, tree } = props;
    const [isOpen, setIsOpen] = openState;

    if (isDesktop) {
        setIsOpen(true);
    }

    /**
     * Toggles the open state of the sidebar.
     */
    const handleToggle = () => {
        clearTimeout(timer);

        setIsOpen(!isOpen);
    };

    /**
     * Creates the sidebar's toggle button.
     * @param onMouseEnter - Callback function for when the mouse enters the button.
     */
    const toggleBtn = () => {
        if (!isDesktop) {
            return (
                <Button
                    sx={toggleBtnSx}
                    onClick={handleToggle}
                    title={t(open ? 'collapse' : 'expand')}
                >
                    <Icon path={isOpen ? mdiPageFirst : mdiPageLast} size={1} />
                </Button>
            );
        }
    };

    /** Button that allows the user to reset the view to the 'all data' option. */
    const allDataButton = (
        <Button sx={getBtnSx(btnConfig.active)} onClick={btnConfig.onClick}>
            {btnConfig.title}
        </Button>
    );

    /** Sidebar styles. */
    const sidebarSx: SxProps = {
        position: 'relative',
        height: '100%',
        flex: 0,
        width: isOpen ? '100%' : 56,
        maxWidth: isOpen ? 'none' : 56,
        flexGrow: 4,
    };

    return (
        <Stack direction='column' sx={sidebarSx}>
            <SidebarHeader divider={isOpen}>
                {isOpen && allDataButton}

                {toggleBtn()}
            </SidebarHeader>
            {isOpen && tree}
        </Stack>
    );
};

/**
 * Interface that describes the props for the Sidebar component.
 */
export interface ISidebarProps {
    /** State slice for controlling the sidebar's open state. */
    openState: [boolean, (boolean) => void];

    /** Configuration for the sidebar's 'all data' button. */
    btnConfig: ISidebarAllDataBtnProps;

    /** TreeView component to render within the sidebar. */
    tree: JSX.Element;
}

/**
 * Interface that describes the props for the Sidebar's 'all data' button.
 */
export interface ISidebarAllDataBtnProps {
    /** If the button should be rendered as active or not. */
    active: boolean;

    /** Button text. */
    title: string;

    /** Button onClick handler. */
    onClick: () => void;
}
